/* eslint-disable import/prefer-default-export */
import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessageType,
    ValidationMessage,
    SettingState,
    Setting,
    TibboDataType,
} from '@appblocks/codegeneration/types';
import ZephyrUtils from '@appblocks/platforms/ZephyrUtils';
import Peripherals from '..';


export const getCodeBlocks = (feature: BaseFeature): CodeBlock[] => {
    const blocks: CodeBlock[] = [];

    const project = feature.project;
    const NAME = feature.getSettingValue('NAME');
    const SPI = feature.getSettingValue('SPI');
    const SPIFeature = project.getFeature(SPI);
    const LCD_SCREEN_WIDGET_COLUMNS = feature.getSettingValue('LCD_SCREEN_WIDGET_COLUMNS');
    const LCD_SCREEN_WIDGET_ROWS = feature.getSettingValue('LCD_SCREEN_WIDGET_ROWS');
    const ADDRESS = feature.getSettingValue('ADDRESS');
    const ROTATION = feature.getSettingValue('ROTATION');
    const MODE = feature.getSettingValue('MODE');
    const CS = feature.getSettingValue('CS');

    let csGPIOSCode = '';
    let dcGPIOSCode = ZephyrUtils.getGPIOPhandleArrayCode(feature, 'DCGPIOS', project);
    let wrGPIOSCode = ZephyrUtils.getGPIOPhandleArrayCode(feature, 'WRGPIOS', project);
    let rdGPIOSCode = ZephyrUtils.getGPIOPhandleArrayCode(feature, 'RDGPIOS', project);
    let resetGPIOSCode = ZephyrUtils.getGPIOPhandleArrayCode(feature, 'RESETGPIOS', project);
    let dataGPIOSCode = '';
    const dataPins = [];
    for (let i = 0; i < 8; i++) {
        const setting = `D${i}`;
        const gpio = feature.getSettingValue(setting);
        if (gpio) {
            dataPins.push(ZephyrUtils.getGPIOCode(gpio, project));
        }
    }
    if (dataPins.length > 0) {
        dataGPIOSCode = dataPins.join(', ');
    }

    if (CS) {
        const csFeature = project.getFeature(CS);
        if (csFeature) {
            const GPIO = Peripherals.getGPIOPeripheral(csFeature.getSettingValue('PIN'), project.device.id);
            csGPIOSCode = `cs-gpios = <&${GPIO.peripheralPort} ${GPIO.num} GPIO_ACTIVE_LOW>;`;
        }
    }

    if (dcGPIOSCode !== '') {
        dcGPIOSCode = `dc-gpios = ${dcGPIOSCode};`;
    }
    if (wrGPIOSCode !== '') {
        wrGPIOSCode = `wr-gpios = ${wrGPIOSCode};`;
    }
    if (rdGPIOSCode !== '') {
        rdGPIOSCode = `rd-gpios = ${rdGPIOSCode};`;
    }
    if (dataGPIOSCode !== '') {
        dataGPIOSCode = `data-gpios = ${dataGPIOSCode};`;
    }
    if (resetGPIOSCode !== '') {
        resetGPIOSCode = `reset-gpios = ${resetGPIOSCode};`;
    }

    let compatible = 'zephyr,mipi-dbi-spi';
    if (MODE === 'parallel') {
        compatible = 'zephyr,mipi-dbi-bitbang';
    }

    blocks.push({
        location: 'APP_OVERLAY_DTS',
        template: `
mipi_dbi {
compatible = "${compatible}";
${dcGPIOSCode}
${wrGPIOSCode}
${rdGPIOSCode}
${csGPIOSCode}
${dataGPIOSCode}
${resetGPIOSCode}
#address-cells = <1>;
#size-cells = <0>;

    st7789v: st7789@${ADDRESS} {
        compatible = "sitronix,st7789v";
        mipi-max-frequency = <30000000>;
        reg = <${ADDRESS}>;
        // vin-supply = <&lcd_bg>;
        width = <${LCD_SCREEN_WIDGET_COLUMNS}>;
        height = <${LCD_SCREEN_WIDGET_ROWS}>;
        x-offset = <${feature.getSettingValue('XOFFSET')}>;
        y-offset = <${feature.getSettingValue('YOFFSET')}>;
        vcom = <${feature.getSettingValue('VCOM')}>;
        gctrl = <${feature.getSettingValue('GCTRL')}>;
        vdvs = <${feature.getSettingValue('VDVS')}>;
        mdac = <${feature.getSettingValue('MDAC')}>;
        gamma = <${feature.getSettingValue('GAMMA')}>;
        colmod = <${feature.getSettingValue('COLMOD')}>;
        lcm = <${feature.getSettingValue('LCM')}>;
        porch-param = [${feature.getSettingValue('PORCH_PARAM')}];
        cmd2en-param = [${feature.getSettingValue('CMD2EN_PARAM')}];
        pwctrl1-param = [${feature.getSettingValue('PWCTRL1_PARAM')}];
        pvgam-param = [${feature.getSettingValue('PVGAM_PARAM')}];
        nvgam-param = [${feature.getSettingValue('NVGAM_PARAM')}];
        ram-param = [${feature.getSettingValue('RAM_PARAM')}];
        rgb-param = [${feature.getSettingValue('RGB_PARAM')}];
        mipi-mode = <${feature.getSettingValue('MIPI_MODE')}>;
    };
};
    `,
    });

    blocks.push({
        location: 'APP_OVERLAY_CHOSEN',
        template: `
zephyr,display = &st7789v;`,
    });

    blocks.push({
        location: 'VARIABLE_DECLARATIONS',
        blockType: BlockType.CODE,
        template: `const struct device *${NAME}_device;`,
    });

    return blocks;
};
